import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { CheckCircle2, XCircle, Ticket } from 'lucide-react';

import { publicActivation } from '../api/endpoints';
import Wordmark from '../components/Wordmark';
import {
  Button, Input, Field, Alert, Card, CardBody, Badge, Skeleton,
} from '../components/ui/primitives';

/**
 * Path 4's redemption page — the one screen in this console reachable with
 * no admin token at all. See jobcard-provisioning-flow.pdf and
 * server/routes/publicActivation.js.
 *
 * Deliberately its OWN page rather than an extension of JobCard/server's own
 * signup form: this build has never modified that repo, and this is where
 * that boundary is held for Path 4. A garage lands here from a link its
 * distributor gave it, creates its account, then signs in at the main app —
 * this page never issues a session of its own.
 */
export default function Activate() {
  const { code } = useParams();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', countryCode: '', address: '', password: '',
  });
  const [result, setResult] = useState(null);

  const preview = useQuery({
    queryKey: ['public-activation', code],
    queryFn: () => publicActivation.preview(code),
    retry: false,
  });

  const redeem = useMutation({
    mutationFn: () => publicActivation.redeem(code, form),
    onSuccess: (res) => setResult(res.data),
  });

  const data = preview.data?.data;
  const valid = data?.valid;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const canSubmit = form.name.trim() && form.email.trim() && form.password.length >= 6;

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center"><Wordmark height={22} /></div>

        <Card>
          <CardBody>
            {preview.isLoading && <Skeleton className="h-40 w-full" />}

            {!preview.isLoading && !valid && (
              <div className="flex flex-col items-center gap-2 py-6 text-center">
                <XCircle className="h-8 w-8 text-red" />
                <p className="text-headline font-semibold text-label">This code isn't valid</p>
                <p className="max-w-xs text-footnote text-label3">
                  {preview.error?.message
                    || 'It may have expired, already been fully used, or been mistyped. Ask whoever gave you this link for a fresh one.'}
                </p>
              </div>
            )}

            {!preview.isLoading && valid && !result && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Ticket className="h-4 w-4 text-blue" />
                  <p className="text-footnote text-label2">
                    Activating a <Badge tone={data.plan === 'pro' ? 'blue' : 'muted'}>{data.plan === 'pro' ? 'JobCard Pro' : 'JobCard Free'}</Badge> account via <strong>{data.distributorName}</strong>
                  </p>
                </div>

                <Field label="Business name"><Input value={form.name} onChange={set('name')} /></Field>
                <Field label="Email"><Input type="email" value={form.email} onChange={set('email')} /></Field>
                <Field label="Phone"><Input value={form.phone} onChange={set('phone')} /></Field>
                <Field label="Country code" hint="e.g. AE"><Input value={form.countryCode} onChange={set('countryCode')} maxLength={2} /></Field>
                <Field label="Address"><Input value={form.address} onChange={set('address')} /></Field>
                <Field label="Password" hint="At least 6 characters. You'll use this to sign in at the main app.">
                  <Input type="password" value={form.password} onChange={set('password')} />
                </Field>

                {redeem.isError && <Alert tone="red">{redeem.error.message}</Alert>}

                <Button className="w-full" disabled={!canSubmit || redeem.isPending} loading={redeem.isPending} onClick={() => redeem.mutate()}>
                  Activate my account
                </Button>
              </div>
            )}

            {result && (
              <div className="flex flex-col items-center gap-2 py-6 text-center">
                <CheckCircle2 className="h-8 w-8 text-green" />
                <p className="text-headline font-semibold text-label">Account created</p>
                <p className="max-w-xs text-footnote text-label3">{result.message}</p>
                {result.downgradedToFree && (
                  <Alert tone="orange" className="mt-2 text-left">
                    Your account was created as Free — the distributor's Pro credit could not be
                    applied. Contact them to resolve it.
                  </Alert>
                )}
                <Link to="/login" className="mt-3 text-footnote font-medium text-blue">
                  Go to sign in →
                </Link>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
